'use client'

import { ProductCardAnimationProvider } from 'product-card/context/ProductCardAnimationContext'
import { ProductCardAnimation } from 'product-card/types'
import { useProductListProvider } from 'product-list/context/ProductListProvider/useProductListProvider'
import { usePreviousUrl } from 'product-list/hooks/usePreviousUrl/usePreviousUrl'
import { View } from 'product-list/types/View'
import { referrerFromProductPage } from 'product-list/utils/referrerFromProductPage'
import { referrerFromSameUrl } from 'product-list/utils/referrerFromSameUrl'
import { type ReactNode, useEffect, useMemo } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import { every } from 'utils/every/every'
import { some } from 'utils/some/some'
import { isLocalStorageAvailable } from 'utils/webStorage/localStorage'

type Props = {
	readonly children: ReactNode
}

export const AddProductCardAnimation = ({ children }: Props) => {
	const { isSmall, isTouchable, isLargeOrGreater } = useResponsive()

	const {
		storedState,
		view,
		shouldRenderSlideshowAnimation,
		slideshowRendered,
		setSlideshowRendered,
	} = useProductListProvider()

	const previousUrl = usePreviousUrl()

	const isReferredFromPDP =
		storedState && previousUrl
			? referrerFromProductPage(storedState.slotId, previousUrl)
			: false

	const isReferredFromSameUrl = previousUrl
		? referrerFromSameUrl(previousUrl)
		: false

	const shouldRenderSlideshow = useMemo(
		() =>
			every(
				isLocalStorageAvailable(),
				shouldRenderSlideshowAnimation,
				!slideshowRendered,
				!isReferredFromPDP,
				!isReferredFromSameUrl,
				view !== View.Overview,
				isTouchable
			),
		[
			shouldRenderSlideshowAnimation,
			isReferredFromPDP,
			isReferredFromSameUrl,
			view,
			isTouchable,
			slideshowRendered,
		]
	)

	const canRenderShortAnimation = some(
		view === View.Inspirational && isSmall,
		view === View.Standard && isLargeOrGreater
	)

	const animation = useMemo(() => {
		if (!shouldRenderSlideshow) {
			return ProductCardAnimation.FadeIn
		}

		if (canRenderShortAnimation) {
			return ProductCardAnimation.ShortSlideshow
		}

		return ProductCardAnimation.LongSlideshow
	}, [shouldRenderSlideshow, canRenderShortAnimation])

	const animationIsSlideshow = some(
		animation === ProductCardAnimation.ShortSlideshow,
		animation === ProductCardAnimation.LongSlideshow
	)

	// If the animation has been rendered, we don't want to set it again
	useEffect(() => {
		if (animationIsSlideshow) {
			setSlideshowRendered()
		}
	}, [animationIsSlideshow, setSlideshowRendered])

	return (
		<ProductCardAnimationProvider animation={animation}>
			{children}
		</ProductCardAnimationProvider>
	)
}
